import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession,
  CognitoIdToken,
  CognitoAccessToken,
  CognitoRefreshToken,
} from 'amazon-cognito-identity-js';
import { ConfirmSignUpCommand, ResendConfirmationCodeCommand, CognitoIdentityProviderClient } from "@aws-sdk/client-cognito-identity-provider";
import {
  resolveCognitoDomain,
  assertRegisteredCognitoDomain,
  hostedUiHost,
} from '../config/cognitoRegistry';
import { getAppMarket } from './MarketService';


const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || 'eu-west-2_dummy', // Prevent crash if env missing
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || 'dummy_client',
};

// Only throw if we actually try to use it with dummies, but constructor won't crash now
export const userPool = new CognitoUserPool(poolData);

// Signup function
export const signUp = (email: string, password: string, givenName: string, familyName: string, phoneNumber: string, units: string, tenantId?: string) => {
  return new Promise((resolve, reject) => {
    const attributeList = [
      new CognitoUserAttribute({ Name: 'email', Value: email }),
      new CognitoUserAttribute({ Name: 'given_name', Value: givenName }),
      new CognitoUserAttribute({ Name: 'family_name', Value: familyName }),
      new CognitoUserAttribute({ Name: 'phone_number', Value: phoneNumber }),
      // new CognitoUserAttribute({ Name: 'custom:units', Value: units }),
    ];
    if (tenantId) {
      attributeList.push(new CognitoUserAttribute({ Name: 'custom:tenantId', Value: tenantId }));
    }
    // if (units) {
    //   attributeList.push(new CognitoUserAttribute({ Name: 'custom:unit', Value: units }));
    // }

    userPool.signUp(email, password, attributeList, [], (err, result) => {
      if (err) reject(err);
      else resolve(result);
    });
  });
};

// Confirm signup (after email verification)
export const confirmSignUp = (email: string, code: string) => {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: userPool });
    user.confirmRegistration(code, true, (err, result) => {
      if (err) reject(err);
      else resolve(result);
    });
  });
};
const client = new CognitoIdentityProviderClient({
  region: process.env.REACT_APP_COGNITO_REGION,              // <-- put your region
});

export async function resendCode(email: string) {
  try {
    const command = new ResendConfirmationCodeCommand({
      ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID, // <-- frontend-safe
      Username: email,
    });

    const resp = await client.send(command);
    const status = resp?.$metadata?.httpStatusCode ?? 0;
    const success = status >= 200 && status < 300;
    const message = success ? "OTP resent to email" : `Failed to resend OTP (status ${status})`;
    return { success, message, data: resp };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
}

// Login function
export const signIn = (email: string, password: string) => {
  return new Promise((resolve, reject) => {
    // Normalize email to lowercase to prevent case-sensitivity issues
    const normalizedEmail = email.trim().toLowerCase();

    const user = new CognitoUser({ Username: normalizedEmail, Pool: userPool });
    const authDetails = new AuthenticationDetails({ Username: normalizedEmail, Password: password });

    user.authenticateUser(authDetails, {
      onSuccess: (session) => resolve(session),
      onFailure: (err) => {
        console.error('[Cognito] Authentication failure:', err);
        reject(err);
      },
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        console.warn('[Cognito] New password required for user');
        // If we don't have a UI for this yet, we reject with a specific code
        // so the LoginForm can guide the user.
        reject({
          code: 'NewPasswordRequired',
          message: 'Password reset required on first login.',
          userAttributes,
          requiredAttributes,
          user
        });
      },
      mfaRequired: (codeName, mfaType) => {
        console.warn('[Cognito] MFA required');
        reject({ code: 'MFARequired', message: 'MFA is required but not supported yet.' });
      }
    });
  });
};

// Complete new password challenge
export const completeNewPassword = (user: any, newPassword: string, attributes: any = {}) => {
  return new Promise((resolve, reject) => {
    // Delete email_verified and phone_number_verified from attributes if they exist
    // as Cognito doesn't like clients trying to update them during passwoed reset
    user.completeNewPasswordChallenge(newPassword, attributes, {
      onSuccess: (session: any) => resolve(session),
      onFailure: (err: any) => {
        console.error('[Cognito] completeNewPassword failure:', err);
        reject(err);
      }
    });
  });
};

// Get current user
export const getCurrentUser = () => userPool.getCurrentUser();

// Logout
export const signOut = () => {
  const user = getCurrentUser();
  if (user) user.signOut();
};

// OAuth URL builder (for manual redirect if not using Hosted UI fully)
// E-58 (F-376): provider union widened to Google | Microsoft | Apple. The
// `texas`/`texas-staging` Hosted-UI domains are NOT registered and are never
// returned by resolveCognitoDomain (S1 regression: no inactive-domain OAuth).
export type SocialProvider = 'Google' | 'Microsoft' | 'Apple';

export interface OAuthUrlOptions {
  /** Send the "name" scope so the IdP returns the user's name on first sign-up. */
  nameScope?: boolean;
  /** Fully override the OAuth scope (default: "openid email profile", plus
   *  "aws.cognito.signin.user.admin" on Texas builds — see getOAuthUrl). */
  scope?: string;
  /** Optional OAuth state token (echoed back on the callback). */
  state?: string;
}

export const getOAuthUrl = (provider: SocialProvider, options?: OAuthUrlOptions) => {
  // Full Hosted-UI HOSTNAME (prefix.auth.<region>.amazoncognito.com). The
  // registry value is a bare prefix; using it as a host 404s at DNS (E-58).
  const host = hostedUiHost(process.env.REACT_APP_COGNITO_DOMAIN);
  const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID || process.env.REACT_APP_CLIENT_ID || '';
  const origin = window.location.origin;
  const redirectUri = encodeURIComponent(`${origin}/auth/callback`);  // Match CDK callbackUrls
  const baseScope =
    provider === 'Apple' && options?.nameScope
      ? 'openid email profile name'
      : 'openid email profile';
  // `aws.cognito.signin.user.admin` authorizes user-pool self-service
  // (GetUserAttributeVerificationCode / VerifyUserAttribute — the
  // "Send Verification Code" flow errors with `Access Token does not have
  // required scopes` without it). Both Texas clients allow the scope; the UK
  // PROD client does not, so requesting it on a UK build fails the whole
  // authorize with `invalid_scope` — gate it to the Texas market build.
  const scope =
    options?.scope ||
    (getAppMarket() === 'texas' ? `${baseScope} aws.cognito.signin.user.admin` : baseScope);
  const state = options?.state ? `&state=${encodeURIComponent(options.state)}` : '';
  return `https://${host}/oauth2/authorize?identity_provider=${provider}&redirect_uri=${redirectUri}&response_type=code&client_id=${clientId}&scope=${encodeURIComponent(scope)}${state}`;
};

/** Apple login is config-gated — hidden until the IdP side is provisioned. */
export const isAppleEnabled = (): boolean => {
  return process.env.REACT_APP_APPLE_ENABLED === 'true';
};

export interface SocialCallbackError {
  type: 'error';
  error: string;
  errorDescription: string;
  cancelled: boolean;
}

export interface SocialCallbackCode {
  type: 'code';
  code: string;
}

export interface SocialCallbackTokens {
  type: 'tokens';
  idToken: string;
  accessToken: string;
  refreshToken?: string;
}

export type SocialCallbackOutcome =
  | SocialCallbackError
  | SocialCallbackCode
  | SocialCallbackTokens
  | { type: 'none' };

/**
 * Interpret the Hosted-UI redirect.
 *  - `?code=...`            → authorization_code (public-client exchange)
 *  - `#id_token=...`        → implicit-flow tokens in the hash
 *  - `?error=access_denied` → user cancelled on the IdP (or a real failure)
 *  - no params              → not a social redirect (email/password flow)
 */
export const parseSocialCallback = (url: string): SocialCallbackOutcome => {
  try {
    const parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'https://local.test');

    const error = parsed.searchParams.get('error');
    if (error) {
      const errorDescription = parsed.searchParams.get('error_description') || '';
      const cancelled = /access_denied|user_cancelled|cancelled/i.test(
        `${error} ${errorDescription}`.toLowerCase()
      );
      return { type: 'error', error, errorDescription, cancelled };
    }

    const code = parsed.searchParams.get('code');
    if (code) {
      return { type: 'code', code };
    }

    const hash = parsed.hash?.replace(/^#/, '') || '';
    if (hash && hash.includes('id_token')) {
      const hashParams = new URLSearchParams(hash);
      const idToken = hashParams.get('id_token');
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token') || undefined;
      if (idToken && accessToken) {
        return { type: 'tokens', idToken, accessToken, refreshToken };
      }
    }

    return { type: 'none' };
  } catch {
    return { type: 'none' };
  }
};

export interface SocialTokenSet {
  id_token: string;
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
}

function parseError(err: any): Error {
  const msg = err && typeof err === 'object'
    ? err.body?.error_description || err.message || 'OAuth token exchange failed'
    : 'OAuth token exchange failed';
  return new Error(String(msg));
}

/**
 * Exchange the Hosted-UI authorization code at the Cognito OAuth2 token
 * endpoint. This is a PUBLIC SPA app client — no client secret is sent — so
 * the exchange is safe to run entirely in the browser (E-58 auth-code path;
 * distinct from the E-039 SAML path in SSOLogin).
 */
export const exchangeAuthCode = async (code: string): Promise<SocialTokenSet> => {
  // Full Hosted-UI HOSTNAME — a bare registry prefix does not resolve, so the
  // exchange failed with "Failed to fetch" on the live us2 lane (E-58).
  const host = hostedUiHost(process.env.REACT_APP_COGNITO_DOMAIN);
  const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID || process.env.REACT_APP_CLIENT_ID || '';
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://local.test';
  const redirectUri = `${origin}/auth/callback`;

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    client_id: clientId,
    redirect_uri: redirectUri,
  });

  let res: Response;
  try {
    res = await fetch(`https://${host}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
  } catch (err) {
    throw parseError(err);
  }

  if (!res.ok) {
    let bodyText = '';
    try {
      bodyText = await res.text();
    } catch {
      /* ignore */
    }
    throw parseError({
      body: { error_description: `Token exchange failed (HTTP ${res.status}): ${bodyText}` },
    });
  }

  try {
    return (await res.json()) as SocialTokenSet;
  } catch (err: any) {
    throw new Error(`Token exchange returned malformed JSON: ${err?.message || ''}`);
  }
};

const SDK_PREFIX = (clientId: string) => `CognitoIdentityServiceProvider.${clientId}`;

/**
 * Persist the social tokens exactly as amazon-cognito-identity-js does, so
 * `getCurrentUser()`/`getSession()` keep working after the OAuth redirect
 * (the SDK ships no parseCognitoWebResponse for code exchange in this
 * version — we rebuild the equivalent storage shape ourselves).
 */
export const establishTokenSession = (
  tokens: { idToken: string; accessToken: string; refreshToken?: string },
  decoded: any
): CognitoUserSession => {
  const session = new CognitoUserSession({
    IdToken: new CognitoIdToken({ IdToken: tokens.idToken }),
    AccessToken: new CognitoAccessToken({ AccessToken: tokens.accessToken }),
    ...(tokens.refreshToken
      ? { RefreshToken: new CognitoRefreshToken({ RefreshToken: tokens.refreshToken }) }
      : {}),
  });

  const clientId = poolData.ClientId || process.env.REACT_APP_CLIENT_ID || 'dummy_client';
  const username = decoded.sub || decoded['cognito:username'] || 'unknown';
  const prefix = SDK_PREFIX(clientId);

  try {
    // App-level token pair (api.ts getAuthToken / refresh)
    localStorage.setItem('idToken', tokens.idToken);
    localStorage.setItem('accessToken', tokens.accessToken);
    if (tokens.refreshToken) localStorage.setItem('refreshToken', tokens.refreshToken);

    // SDK-shaped session (userPool.getCurrentUser() / getSession())
    localStorage.setItem(`${prefix}.LastAuthUser`, username);
    localStorage.setItem(`${prefix}.${username}.idToken`, tokens.idToken);
    localStorage.setItem(`${prefix}.${username}.accessToken`, tokens.accessToken);
    if (tokens.refreshToken) {
      localStorage.setItem(`${prefix}.${username}.refreshToken`, tokens.refreshToken);
    }
    localStorage.setItem(`${prefix}.${username}.clockDrift`, '0');
  } catch {
    /* private-mode storage errors are non-fatal — idToken/accessToken also live above */
  }

  return session;
};

/** Read the LINK_REQUIRED signal from a social idToken (F-378 / US-265). */
export const getLinkRequired = (decoded: any): { required: boolean; email?: string } => {
  const required =
    decoded?.['custom:link_required'] === 'true' || decoded?.link_required === true;
  return { required, email: decoded?.['custom:link_email'] || decoded?.email };
};

/** Resolved active Hosted-UI domain for this build (exported for tooling/tests). */
export const getActiveCognitoDomain = (): string =>
  assertRegisteredCognitoDomain(resolveCognitoDomain(process.env.REACT_APP_COGNITO_DOMAIN));


// Forgot Password (initiate)
export const forgotPassword = (email: string) => {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: userPool });
    user.forgotPassword({
      onSuccess: (data) => resolve(data),
      onFailure: (err) => reject(err),
    });
  });
};

// Confirm Password (finalize with code)
export const confirmPassword = (email: string, code: string, newPassword: string) => {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: userPool });
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve("Password changed successfully"),
      onFailure: (err) => reject(err),
    });
  });
};

// Request Phone Verification Code
export const requestPhoneVerification = () => {
  return new Promise((resolve, reject) => {
    const user = userPool.getCurrentUser();
    if (!user) return reject(new Error("No user logged in"));

    user.getSession((err: any, session: any) => {
      if (err) return reject(err);

      user.getAttributeVerificationCode('phone_number', {
        onSuccess: (result) => resolve(result),
        onFailure: (err) => reject(err),
        inputVerificationCode: () => resolve('Input verification code'), // Some flows trigger this
      });
    });
  });
};

// Confirm Phone Verification Code
export const confirmPhoneVerification = (code: string) => {
  return new Promise((resolve, reject) => {
    const user = userPool.getCurrentUser();
    if (!user) return reject(new Error("No user logged in"));

    user.getSession((err: any, session: any) => {
      if (err) return reject(err);

      user.verifyAttribute('phone_number', code, {
        onSuccess: (result) => resolve(result),
        onFailure: (err) => reject(err),
      });
    });
  });
};

// Send email verification code (Cognito sends a 6-digit code to the user's email)
export const sendEmailVerificationCode = () => {
  return new Promise((resolve, reject) => {
    const user = userPool.getCurrentUser();
    if (!user) return reject(new Error('No user logged in'));

    user.getSession((err: any, session: any) => {
      if (err) return reject(err);

      user.getAttributeVerificationCode('email', {
        onSuccess: (result) => resolve(result),
        onFailure: (err) => reject(err),
        inputVerificationCode: () => resolve('Code sent'),
      });
    });
  });
};

// Verify the email code entered by the user
export const verifyEmailCode = (code: string) => {
  return new Promise((resolve, reject) => {
    const user = userPool.getCurrentUser();
    if (!user) return reject(new Error('No user logged in'));

    user.getSession((err: any, session: any) => {
      if (err) return reject(err);

      user.verifyAttribute('email', code, {
        onSuccess: (result) => resolve(result),
        onFailure: (err) => reject(err),
      });
    });
  });
};

// Get the Cognito access token string (needed for backend verify-email call)
export const getAccessToken = (): Promise<string> => {
  return new Promise((resolve, reject) => {
    const user = userPool.getCurrentUser();
    if (!user) return reject(new Error('No user logged in'));

    user.getSession((err: any, session: any) => {
      if (err) return reject(err);
      resolve(session.getAccessToken().getJwtToken());
    });
  });
};

// Get User Attributes to check verification status
export const getUserAttributes = () => {
  return new Promise<any>((resolve, reject) => {
    const user = userPool.getCurrentUser();
    if (!user) return reject(new Error("No user logged in"));

    user.getSession((err: any, session: any) => {
      if (err) return reject(err);

      user.getUserAttributes((err, attributes) => {
        if (err) return reject(err);
        const attrMap: any = {};
        attributes?.forEach(attr => {
          attrMap[attr.getName()] = attr.getValue();
        });
        resolve(attrMap);
      });
    });
  });
};
